import type { ClassSeasonDetails } from './server/class-season.loader';

export interface ClassSeasonCatalogChoice {
  id: string;
  /** JavaScript weekday: Sunday = 0, Saturday = 6. */
  weekday: number;
  startTime: string;
  endTime: string;
  /** A choice always buys this complete season, including every remaining day. */
  season: ClassSeasonDetails;
}

export interface ClassSeasonCatalogTerm {
  id: string;
  startsOn: string;
  endsOn: string;
  choices: ClassSeasonCatalogChoice[];
}

export interface ClassSeasonCatalogClass {
  classTypeId: string;
  className: string;
  classNameAlt: string | null;
  description: string | null;
  descriptionAlt: string | null;
  imageUrl: string | null;
  terms: ClassSeasonCatalogTerm[];
}

/** One choice per upcoming weekday + start time of the season. */
export function getSeasonScheduleChoices(season: ClassSeasonDetails) {
  const choices = new Map<string, ClassSeasonCatalogChoice>();
  const sessions = season.quote.sessions
    .filter((session) => !session.is_past)
    .sort((left, right) =>
      left.date.localeCompare(right.date) ||
      left.start_time.localeCompare(right.start_time),
    );

  for (const session of sessions) {
    const date = new Date(`${session.date}T12:00:00Z`);
    const weekday = date.getUTCDay();
    if (!Number.isFinite(weekday) || date.toISOString().slice(0, 10) !== session.date) {
      continue;
    }

    const id = `${season.classScheduleId}:${weekday}:${session.start_time}`;
    if (!choices.has(id)) {
      choices.set(id, {
        id,
        weekday,
        startTime: session.start_time,
        endTime: session.end_time,
        season,
      });
    }
  }

  return [...choices.values()];
}

/** Groups class offers into date-window terms with only real upcoming slots. */
export function getClassSeasonCatalog(seasons: ClassSeasonDetails[]) {
  const classes = new Map<string, ClassSeasonCatalogClass>();

  for (const season of seasons) {
    let classGroup = classes.get(season.classTypeId);
    if (!classGroup) {
      classGroup = {
        classTypeId: season.classTypeId,
        className: season.className,
        classNameAlt: season.classNameAlt,
        description: season.description,
        descriptionAlt: season.descriptionAlt,
        imageUrl: season.imageUrl,
        terms: [],
      };
      classes.set(season.classTypeId, classGroup);
    }

    const id = `${season.quote.starts_on}:${season.quote.ends_on}`;
    let term = classGroup.terms.find((entry) => entry.id === id);
    if (!term) {
      term = {
        id,
        startsOn: season.quote.starts_on,
        endsOn: season.quote.ends_on,
        choices: [],
      };
      classGroup.terms.push(term);
    }

    const existingIds = new Set(term.choices.map((choice) => choice.id));
    term.choices.push(...getSeasonScheduleChoices(season).filter((choice) => !existingIds.has(choice.id)));
  }

  for (const classGroup of classes.values()) {
    classGroup.terms.sort((left, right) =>
      left.startsOn.localeCompare(right.startsOn) ||
      left.endsOn.localeCompare(right.endsOn),
    );
    for (const term of classGroup.terms) {
      term.choices.sort((left, right) =>
        left.weekday - right.weekday ||
        left.startTime.localeCompare(right.startTime) ||
        left.season.classScheduleId.localeCompare(right.season.classScheduleId),
      );
    }
  }

  return [...classes.values()];
}
